import DataTable from 'components/DataTable';
import useUser from 'hooks/useUser';
import { PropTypes } from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pagination } from 'flowbite-react';
import { formatDate } from 'utils/utils';
import {
  getDocumentsByType,
  getDocumentTypeSummaryLabel,
  fileListFilterDefaults,
  pageData,
  urlRouter,
  statusFile,
} from 'utils/constants';
import { useGetListFiles } from 'hooks';
import { useNavigatePage } from 'hooks/useNavigatePage';

const FileListPageContent = ({
  searchQuery,
  documentType,
  filters,
  currentPage,
  onPageChange,
}) => {
  const { handleNavigate } = useNavigatePage(urlRouter.FILE_REVIEW_PAGE);
  const [pageCount, setPageCount] = useState(1);
  const [enableFetching, setEnableFetching] = useState(false);
  const { userToken } = useUser();

  const handleStatus = useCallback(status => {
    // eslint-disable-next-line default-case
    switch (status) {
      case statusFile.OPEN:
        return statusFile.OPEN.toUpperCase();
      case statusFile.SAVED_REVIEW_SHEET:
        return statusFile.REPORT_SAVED;
      case statusFile.CLOSED:
        return statusFile.CLOSED.toUpperCase();
    }
  }, []);

  useEffect(() => {
    userToken && setEnableFetching(true);
  }, [userToken]);

  const { isLoading, isFetching, data, isError } = useGetListFiles(
    currentPage,
    searchQuery,
    documentType,
    filters,
    userToken,
    enableFetching,
  );

  const fileEntries = useMemo(
    () =>
      data
        ? data.data.entities
            .map(entity => {
              const matchingDocuments = getDocumentsByType(entity.documents, documentType);
              const verifiedDocuments = matchingDocuments.filter(document => document.verified);

              if (matchingDocuments.length === 0) {
                return null;
              }

              return {
                id: entity.id,
                fileNumber: entity.reswareFileNumber,
                address: entity.address,
                statusTitle: handleStatus(entity.status),
                file: `${matchingDocuments.length} ${getDocumentTypeSummaryLabel(
                  documentType,
                  matchingDocuments.length,
                )}, ${verifiedDocuments.length} Verified`,
                date: formatDate(entity.created),
                status: entity.status,
                isEmpty: false,
              };
            })
            .filter(Boolean)
        : [],
    [data, documentType, handleStatus],
  );

  useEffect(() => {
    const pageCountTemp = data ? Math.ceil(data.data.totalEntities / 10) : 1;
    setPageCount(pageCountTemp);
  }, [data]);

  if (isLoading || isFetching) {
    return (
      <span data-testid="test-loader" className="w-full flex justify-center">
        Files Loading...
      </span>
    );
  }

  if (isError) {
    return (
      <span data-testid="test-error" className="w-full flex justify-center">
        An Error occured When fetching the data
      </span>
    );
  }

  return (
    <div>
      <div className="mb-8 block">
        <DataTable
          columns={pageData.FILE_LIST.TABLE_COLUMNS_DATA}
          data={fileEntries}
          onRowClicked={handleNavigate}
        />
      </div>
      <div
        className={`flex items-center justify-center mb-8 ${pageCount <= 1 && 'hidden'}`}
      >
        <Pagination
          data-testid="test-pagination"
          id="test-pagination"
          currentPage={currentPage}
          totalPages={pageCount}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
};

FileListPageContent.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  documentType: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    status: PropTypes.string,
    sortDirection: PropTypes.string,
  }),
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
};

FileListPageContent.defaultProps = {
  filters: fileListFilterDefaults,
  currentPage: 1,
  onPageChange: () => {},
};

export default FileListPageContent;
